import { UserContext } from "@/context/UserContext";
import { useIsInMockScope } from "@/libs/mockScope";
import { ConfigParameters } from "@/types/api";

/**
 * The new agent experience (`isNewAgentOnboarding`) runs in one of two modes:
 *
 * - **demo** (`isAgentsMock`): its routes render under `<MockScope>`, so every
 *   agent endpoint - shipped or not - is served from the mock stores and the
 *   whole click-through works with no backend.
 * - **live**: no `<MockScope>`, so every call is real, and a feature whose API
 *   has not shipped is hidden or disabled (see `@/configs/agentFeatures`).
 *
 * `isAgentsMock` is the only agent demo signal: the demo hostname
 * (`isDemoEnvironment`) does not affect agent endpoints.
 */
export type AgentsMode = "demo" | "live";

/**
 * Non-hook form, for route helpers, loaders, menu filters and mock seeds.
 * Decides whether the agent routes mount `<MockScope>` at all. Components that
 * already hold `useConfigParameters()` pass it in instead.
 */
export const isAgentsDemoMode = (
  params: ConfigParameters = UserContext.getConfigParameters(),
): boolean => !!params.isAgentsMock;

/**
 * Hook form, for components. Inside the app the agent routes mount
 * `<MockScope>` exactly when `isAgentsDemoMode()` holds, so the scope is the
 * runtime signal - which also lets a component spec pick the mode with
 * `mountWithPage(..., { mockScope })` alone.
 */
export const useAgentsMode = (): AgentsMode =>
  useIsInMockScope() ? "demo" : "live";
